import React from 'react';
import { X, Calendar, Users, Trophy, Target, Award, Settings, Trash2, Plus } from 'lucide-react';
import { Badge } from './ui/badge';
import { EventBounties } from './EventBounties';

interface EventDetailsModalProps {
    project: any;
    onClose: () => void;
    onEdit: (project: any) => void;
    onDelete: (projectId: string) => void;
    onCreateBounty: (project: any) => void;
    bountiesLastUpdated: number;
}

const EventDetailsModal: React.FC<EventDetailsModalProps> = ({
    project,
    onClose,
    onEdit,
    onDelete,
    onCreateBounty,
    bountiesLastUpdated
}) => {
    const formatDate = (date: Date | string) => {
        if (!date) return "N/A";
        const d = new Date(date);
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    const getStatusBadgeStyle = (status: string) => {
        const statusLower = status.toLowerCase();
        switch (statusLower) {
            case "draft": return "bg-gray-100 text-gray-800 border-gray-300";
            case "published": return "bg-green-100 text-green-800 border-green-300";
            case "ongoing": return "bg-blue-100 text-blue-800 border-blue-300";
            case "completed": return "bg-purple-100 text-purple-800 border-purple-300";
            case "cancelled": return "bg-red-100 text-red-800 border-red-300";
            default: return "bg-gray-100 text-gray-800 border-gray-300";
        }
    };

    return (
        <div
            className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm overflow-y-auto"
            onClick={onClose}
            style={{ overflowY: 'auto' }}
        >
            <div className="min-h-screen flex items-center justify-center p-4">
                <div
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-8"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="p-6 sm:p-8">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-4 mb-6">
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">{project.title}</h2>
                                    <Badge className={`${getStatusBadgeStyle(project.status)} border font-semibold text-xs uppercase px-2.5 py-0.5`}>
                                        {project.status}
                                    </Badge>
                                </div>
                                {project.theme && (
                                    <div className="flex flex-wrap gap-1.5">
                                        {project.theme.split(/[&,]+/).map((tag: string, i: number) => (
                                            <span key={i} className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${['bg-blue-50 text-blue-700 border-blue-200', 'bg-green-50 text-green-700 border-green-200', 'bg-purple-50 text-purple-700 border-purple-200', 'bg-pink-50 text-pink-700 border-pink-200', 'bg-amber-50 text-amber-700 border-amber-200'][Math.abs(tag.split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % 5]}`}>
                                                {tag.trim()}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                                <button
                                    onClick={onClose}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <X className="h-6 w-6 text-gray-500" />
                                </button>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2 mb-8">
                            <button
                                onClick={() => onCreateBounty(project)}
                                className="flex items-center gap-2 px-4 py-2 bg-[#782c8c] text-white rounded-lg hover:opacity-90 transition-all text-sm font-medium shadow-sm"
                            >
                                <Plus className="h-4 w-4" />
                                Create Bounty
                            </button>
                            <button
                                onClick={() => onEdit(project)}
                                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-all text-sm font-medium shadow-sm"
                            >
                                <Settings className="h-4 w-4" />
                                Edit Event
                            </button>
                            <button
                                onClick={() => onDelete(project.id)}
                                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-red-600 rounded-lg hover:bg-red-50 transition-all text-sm font-medium shadow-sm"
                            >
                                <Trash2 className="h-4 w-4" />
                                Delete
                            </button>
                        </div>

                        {/* Description */}
                        {project.description && (
                            <div className="mb-8">
                                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-2">Description</h3>
                                <p className="text-gray-600 leading-relaxed">{project.description}</p>
                            </div>
                        )}

                        {/* Stats Grid */}
                        <div className="gap-6 mb-8 p-6 bg-gray-50 rounded-xl border border-gray-100">
                            {/* Dates */}
                            <div className="flex flex-col gap-3">
                                <div className="flex items-center gap-3 text-sm">
                                    <Calendar className="h-4 w-4 text-gray-400" />
                                    <span className="text-gray-900 font-medium">
                                        {formatDate(project.startDate)} - {formatDate(project.endDate)}
                                    </span>
                                </div>
                                {project.registrationDeadline && (
                                    <div className="flex items-center gap-3 text-sm">
                                        <Target className="h-4 w-4 text-gray-400" />
                                        <span className="text-gray-900 font-medium">
                                            {formatDate(project.registrationDeadline)}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Metrics */}
                            <div className="flex flex-col gap-1 py-2">
                                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider text-left">Participants <span className="text-sm font-bold text-gray-900">{project.currentParticipants}</span>
                                    <span className="text-xs text-gray-400">/{project.maxParticipants}</span></span>
                            </div>
                            <div className="flex flex-col gap-1 py-1">
                                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider text-left">Bounties <span className="text-sm font-bold text-gray-900">{project.activeBounties}</span>
                                    <span className="text-xs text-gray-400">/{project.totalBounties}</span></span>
                            </div>
                        </div>

                        {/* Event Bounties Section */}
                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                    <Award className="h-5 w-5 text-amber-600" />
                                    Event Bounties
                                </h4>
                            </div>
                            <EventBounties eventId={project.id} lastUpdated={bountiesLastUpdated} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventDetailsModal;
